import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrderParty, getRooms, getTables, getOrders, getOrder, mergeOrders } from '../../lib/api';
import { useCart } from '../../contexts/CartContext';
import { useDrawer } from '../PosLayout';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Loader2, Search, ClipboardList, FileText, RefreshCw, UsersRound, X
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Color helpers ─────────────────────────────────────────────────────── */
const BAR_KEYWORDS = ['theke', 'bar', 'counter', 'buffet'];

function tableFloorColor(table, isBarRoom) {
  if (table.status === 'occupied') return 'bg-rose-400 border-rose-500 text-white';
  if (table.status === 'reserved') return 'bg-amber-400 border-amber-500 text-white';
  if (isBarRoom)                   return 'bg-teal-500 border-teal-600 text-white';
  return 'bg-card border-border text-foreground';
}

function tableListColor(table) {
  if (table.status === 'occupied') return 'bg-rose-400 text-white';
  if (table.status === 'reserved') return 'bg-amber-400 text-white';
  return 'bg-card text-muted-foreground';
}

/* ─── Floor plan ────────────────────────────────────────────────────────── */
function FloorPlan({ tables, selectedId, onSelect, isBarRoom, openOrderTableIds }) {
  if (tables.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-sm">Keine Tische in diesem Raum</p>
      </div>
    );
  }

  const cols = Math.max(3, Math.ceil(Math.sqrt(tables.length)));

  return (
    <div className="relative w-full h-full">
      {tables.map((table, index) => {
        const hasCoords = (table.x > 0 || table.y > 0);
        const col = index % cols;
        const row = Math.floor(index / cols);
        const totalRows = Math.ceil(tables.length / cols);

        const leftPct = hasCoords
          ? `${Math.min(Math.max(table.x, 5), 85)}%`
          : `${8 + col * (80 / Math.max(cols - 1, 1))}%`;
        const topPct = hasCoords
          ? `${Math.min(Math.max(table.y, 5), 85)}%`
          : `${10 + row * (72 / Math.max(totalRows - 1, 1))}%`;

        const isRound    = table.shape === 'round';
        const isSelected = selectedId === table.id;

        return (
          <button
            key={table.id}
            onClick={() => onSelect(table)}
            style={{ left: leftPct, top: topPct, position: 'absolute', transform: 'translate(-50%, -50%)' }}
            className={`
              w-20 h-20 border-2 flex flex-col items-center justify-center
              transition-colors duration-150 active:scale-95
              ${isRound ? 'rounded-full' : 'rounded-md'}
              ${tableFloorColor(table, isBarRoom)}
              ${isSelected ? 'ring-4 ring-blue-400 ring-offset-2' : ''}
            `}
          >
            {openOrderTableIds?.has(table.id) && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-400 rounded-full border border-white" />
            )}
            <span className="text-[10px] opacity-70 leading-none">
              #{table.sort_order ?? ''}
            </span>
            <span className="font-bold text-xs text-center leading-tight px-1 mt-0.5">
              {table.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Cart / info panel ─────────────────────────────────────────────────── */
function CartPanel({ selectedTable }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <ClipboardList className="w-16 h-16 text-slate-200 mb-4 flex-shrink-0" />
        {selectedTable ? (
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-muted-foreground block mb-1">{selectedTable.name}</span>
            ausgewählt — Artikel hinzufügen oder Zahlen drücken.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Keine Artikel hinzugefügt. Bitte zuerst auswählen oder scannen.
          </p>
        )}
      </div>
      {selectedTable && (
        <div className="border-t border-border px-4 py-3 flex-shrink-0">
          <p className="text-xs text-muted-foreground">Tisch</p>
          <p className="font-bold text-foreground">{selectedTable.name}</p>
          <p className="text-xs text-muted-foreground capitalize mt-0.5">
            {selectedTable.status === 'free'     ? 'Frei' :
             selectedTable.status === 'occupied' ? 'Besetzt' : 'Reserviert'}
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */
export default function PosTables() {
  const [rooms, setRooms]                 = useState([]);
  const [tables, setTables]               = useState([]);
  const [isLoading, setIsLoading]         = useState(true);
  const [activeRoomId, setActiveRoomId]   = useState(null);
  const [localSelected, setLocalSelected] = useState(null);
  const [searchOpen, setSearchOpen]         = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [quickNumber, setQuickNumber]       = useState('');
  const [openOrderTableIds, setOpenOrderTableIds] = useState(new Set());
  const [partyPicker, setPartyPicker]       = useState(null);
  const [isCreatingParty, setIsCreatingParty] = useState(false);
  const [mergePicker, setMergePicker]       = useState(null);
  const [isMergingParty, setIsMergingParty] = useState(false);

  const navigate = useNavigate();
  const { setSelectedTable, loadOrder, clearCart } = useCart();
  const drawer   = useDrawer();          // available if caller needs it

  /* ── load tables + open-order indicators ── */
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [roomsData, tablesData] = await Promise.all([getRooms(), getTables()]);
      setRooms(roomsData);
      setTables(tablesData);
      if (roomsData.length > 0) setActiveRoomId(r => r ?? roomsData[0].id);
    } catch {
      toast.error('Fehler beim Laden der Tische');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadOpenOrders = useCallback(async () => {
    try {
      const orders = await getOrders('open');
      setOpenOrderTableIds(new Set(orders.map(o => o.table_id).filter(Boolean)));
    } catch {
      // non-critical — silently ignore
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadOpenOrders(); }, [loadOpenOrders]);

  /* ── derived ── */
  const activeRoom  = rooms.find(r => r.id === activeRoomId);
  const roomTables  = tables.filter(t => t.room_id === activeRoomId);
  const isBarRoom   = activeRoom
    ? BAR_KEYWORDS.some(k => activeRoom.name.toLowerCase().includes(k))
    : false;

  const filteredList = searchQuery
    ? roomTables.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : roomTables;

  /* ── handlers ── */
  const handleSelect = (table) => {
    // Clicking the already-selected table again deselects it (no table chosen).
    if (localSelected?.id === table.id) {
      setLocalSelected(null);
      setSelectedTable(null);
      return;
    }
    setLocalSelected(table);
    setSelectedTable(table);
  };

  const handleDeselect = () => {
    setLocalSelected(null);
    setSelectedTable(null);
  };

  // Quick number entry: type a table number (e.g. "12") + Enter to jump straight
  // to it, searching across all rooms — faster than browsing room tabs visually.
  const handleQuickNumberSubmit = (e) => {
    e.preventDefault();
    const query = quickNumber.trim().toLowerCase();
    if (!query) return;

    const match = tables.find((t) => {
      const name = t.name.toLowerCase();
      const digits = name.replace(/\D+/g, '');
      return name === query || digits === query || name.includes(query);
    });

    if (!match) {
      toast.warning(`Kein Tisch "${quickNumber}" gefunden`);
      return;
    }

    if (match.room_id !== activeRoomId) setActiveRoomId(match.room_id);
    handleSelect(match);
    setQuickNumber('');
  };

  const openOrder = async (order, { payment = false } = {}) => {
    const full = await getOrder(order.id);
    loadOrder(full);
    navigate('/pos/terminal', {
      state: payment ? {
        openPayment: true,
        paymentIntentId: `table-payment-${Date.now()}`,
        defaultMethod: null,
        paymentOrder: full,
      } : undefined,
    });
  };

  const chooseTableOrder = async (mode) => {
    if (!localSelected) return;
    try {
      const orders = await getOrders('open', null, localSelected.id);
      if (orders.length === 0) {
        if (mode === 'payment') {
          toast.warning('Für diesen Tisch gibt es noch keine offene Bestellung.');
          return;
        }
        clearCart();
        setSelectedTable(localSelected);
        navigate('/pos/terminal');
        return;
      }
      if (orders.length === 1) {
        await openOrder(orders[0], { payment: mode === 'payment' });
        return;
      }
      setPartyPicker({ mode, orders });
    } catch {
      toast.error('Fehler beim Laden der Bestellung');
    }
  };

  const handleBestellen = () => {
    if (!localSelected) { toast.warning('Bitte zuerst einen Tisch auswählen'); return; }
    chooseTableOrder('order');
  };

  const handleDirekt = () => {
    clearCart();
    navigate('/pos/terminal');
  };

  const handleZahlen = () => {
    if (!localSelected) { toast.warning('Bitte zuerst einen Tisch auswählen'); return; }
    chooseTableOrder('payment');
  };

  const handleCreateParty = async () => {
    if (!partyPicker?.orders?.[0]) return;
    setIsCreatingParty(true);
    try {
      const party = await createOrderParty(partyPicker.orders[0].id);
      setPartyPicker(null);
      await openOrder(party);
      loadOpenOrders();
    } catch {
      toast.error('Neue Partei konnte nicht angelegt werden');
    } finally {
      setIsCreatingParty(false);
    }
  };

  const handleMergeParty = async (sourceOrder, targetOrder) => {
    setIsMergingParty(true);
    try {
      const merged = await mergeOrders(sourceOrder.id, targetOrder.id);
      setMergePicker(null);
      setPartyPicker(null);
      await Promise.all([loadData(), loadOpenOrders()]);
      toast.success('Parteien wurden zusammengeführt');
      await openOrder(merged);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Parteien konnten nicht zusammengeführt werden');
    } finally {
      setIsMergingParty(false);
    }
  };

  /* ── loading ── */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-muted" data-testid="pos-tables">

      <Dialog open={Boolean(partyPicker)} onOpenChange={(open) => !open && setPartyPicker(null)}>
        <DialogContent className="pos-workflow-dialog max-w-lg mx-4 rounded-none border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UsersRound className="h-5 w-5" />Offene Parteien</DialogTitle>
            <DialogDescription>
              {localSelected?.name}: Wählen Sie den Vorgang, den Sie {partyPicker?.mode === 'payment' ? 'kassieren' : 'weiter bonieren'} möchten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {partyPicker?.orders.map((order, index) => (
              <button
                key={order.id}
                onClick={async () => {
                  const mode = partyPicker.mode;
                  setPartyPicker(null);
                  await openOrder(order, { payment: mode === 'payment' });
                }}
                className="pos-dialog-secondary flex items-center justify-between gap-3 text-left"
              >
                <span>
                  <span className="block font-bold">{order.notes?.match(/Partei[^|]*/)?.[0]?.trim() || `Partei ${index + 1}`}</span>
                  <span className="block text-sm text-muted-foreground">{order.items?.length || 0} Artikel · offen</span>
                </span>
                <span className="font-bold tabular-nums">€{Number(order.total || 0).toFixed(2)}</span>
              </button>
            ))}
          </div>
          {partyPicker?.mode === 'order' && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleCreateParty}
                disabled={isCreatingParty}
                className="pos-dialog-primary"
              >
                {isCreatingParty ? 'Neue Partei wird angelegt...' : 'Neue Partei anlegen'}
              </button>
              {partyPicker.orders.length > 1 && (
                    <button
                      onClick={() => {
                        setMergePicker({ orders: partyPicker.orders, source: null });
                        setPartyPicker(null);
                      }}
                  className="pos-dialog-secondary"
                >
                  Parteien zusammenführen
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(mergePicker)} onOpenChange={(open) => !open && setMergePicker(null)}>
        <DialogContent className="pos-workflow-dialog max-w-lg mx-4 rounded-none border">
          <DialogHeader>
            <DialogTitle>Parteien zusammenführen</DialogTitle>
            <DialogDescription>
              {mergePicker?.source
                ? 'Wählen Sie die Ziel-Partei. Die Positionen der ersten Auswahl werden dorthin übertragen.'
                : 'Wählen Sie zuerst die Partei, deren Positionen übertragen werden sollen.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {mergePicker?.orders
              .filter((order) => order.id !== mergePicker.source?.id)
              .map((order, index) => (
                <button
                  key={order.id}
                  disabled={isMergingParty}
                  onClick={() => (
                    mergePicker.source
                      ? handleMergeParty(mergePicker.source, order)
                      : setMergePicker((current) => ({ ...current, source: order }))
                  )}
                  className="pos-dialog-secondary flex items-center justify-between gap-3 text-left"
                >
                  <span>
                    <span className="block font-bold">{order.notes?.match(/Partei[^|]*/)?.[0]?.trim() || `Partei ${index + 1}`}</span>
                    <span className="block text-sm text-muted-foreground">{order.items?.length || 0} Artikel · €{Number(order.total || 0).toFixed(2)}</span>
                  </span>
                  <span className="text-sm font-semibold">{mergePicker.source ? 'Hierhin führen' : 'Als Quelle wählen'}</span>
                </button>
              ))}
          </div>
          {mergePicker?.source && (
            <button onClick={() => setMergePicker((current) => ({ ...current, source: null }))} className="pos-dialog-secondary w-full">
              Andere Quell-Partei wählen
            </button>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Room selector + actions header ── */}
      <div className="bg-card border-b border-border px-2 py-2 md:px-3 md:py-2 flex items-center gap-1.5 md:gap-2 flex-shrink-0">
        <div className="flex-1 flex items-center gap-1 overflow-x-auto no-scrollbar">
          {rooms.map(room => (
            <button
              key={room.id}
              onClick={() => { setActiveRoomId(room.id); setLocalSelected(null); setSearchQuery(''); }}
              className={`px-2.5 py-1 text-xs md:px-3 md:py-1.5 md:text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                activeRoomId === room.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {room.name}
            </button>
          ))}
        </div>
        <form onSubmit={handleQuickNumberSubmit} className="flex-shrink-0">
          <input
            inputMode="numeric"
            value={quickNumber}
            onChange={(e) => setQuickNumber(e.target.value)}
            placeholder="Tisch-Nr."
            className="w-20 md:w-24 text-xs md:text-sm border border-border px-2 py-1.5 outline-none focus:border-primary bg-muted"
            data-testid="quick-table-number"
          />
        </form>
        {localSelected ? (
          <button
            onClick={handleDeselect}
            className="flex items-center gap-1 px-2 py-1.5 text-xs md:text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors flex-shrink-0"
            data-testid="deselect-table"
          >
            <X className="w-3.5 h-3.5" />
            Auswahl
          </button>
        ) : null}
        <button onClick={() => setSearchOpen(v => !v)}
          className="p-1.5 hover:bg-muted transition-colors flex-shrink-0">
          <Search className="w-4 h-4 text-muted-foreground" />
        </button>
        <button onClick={() => { loadData(); loadOpenOrders(); }}
          className="p-1.5 hover:bg-muted transition-colors flex-shrink-0">
          <RefreshCw className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="bg-card border-b border-border px-2 py-2 md:px-3 md:py-2 flex-shrink-0">
          <input
            autoFocus
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Tisch suchen…"
            className="w-full text-sm border border-border px-3 py-1.5 outline-none focus:border-primary bg-muted"
          />
        </div>
      )}

      {/* Status legend */}
      <div className="hidden lg:flex bg-card border-b border-border px-4 py-1 items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-card border border-border inline-block" />Frei
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-rose-400 inline-block" />Besetzt
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />Reserviert
        </span>
        {isBarRoom && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-teal-500 inline-block" />Theke
          </span>
        )}
      </div>

      {/* ── 3-column body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left: table list (full width on mobile, fixed column from lg+) */}
        <div className="w-full lg:w-44 border-r border-border bg-card overflow-y-auto lg:flex-shrink-0">
          {filteredList.length === 0
            ? <p className="p-4 text-xs text-muted-foreground text-center">Keine Tische</p>
            : filteredList.map(table => (
              <button
                key={table.id}
                onClick={() => handleSelect(table)}
                className={`
                  w-full text-left px-3 py-2 border-b border-border transition-colors relative
                  ${tableListColor(table)}
                  ${localSelected?.id === table.id ? 'ring-2 ring-inset ring-blue-400' : ''}
                `}
              >
                {openOrderTableIds.has(table.id) && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-green-400 rounded-full" />
                )}
                <p className="text-[10px] opacity-60">#{table.sort_order ?? ''}</p>
                <p className="font-semibold text-sm leading-tight">{table.name}</p>
              </button>
            ))
          }
        </div>

        {/* Center: floor plan (desktop only — list is the primary mobile UI) */}
        <div className="hidden lg:block relative flex-1 m-2 bg-card border border-border overflow-hidden">
          <FloorPlan
            tables={filteredList}
            selectedId={localSelected?.id}
            onSelect={handleSelect}
            isBarRoom={isBarRoom}
            openOrderTableIds={openOrderTableIds}
          />
        </div>

        {/* Right: cart panel (desktop only) */}
        <div className="hidden lg:flex w-60 border-l border-border bg-card flex-col flex-shrink-0">
          <CartPanel selectedTable={localSelected} />
        </div>
      </div>

      {/* ── Bottom action bar ── */}
      <div className="flex h-16 flex-shrink-0 items-stretch border-t border-border bg-muted lg:h-14">
        <button
          onClick={handleDirekt}
          className="flex flex-1 flex-col items-center justify-center gap-1 border-r border-border transition-colors hover:bg-card"
        >
          <FileText className="h-4 w-4 text-muted-foreground lg:h-4 lg:w-4" />
          <span className="text-[11px] font-semibold text-muted-foreground lg:text-xs">DIREKT</span>
        </button>

        <button
          onClick={handleBestellen}
          className="flex flex-[2] flex-col items-center justify-center border-r border-border transition-colors hover:bg-card"
        >
          <span className="text-base font-bold text-[#24323b] lg:text-sm">Bestellen</span>
          <span className="text-[11px] text-muted-foreground lg:text-xs">NEUE ARTIKEL</span>
        </button>

        <button
          onClick={handleZahlen}
          className="flex flex-[2] flex-col items-center justify-center bg-primary text-primary-foreground transition-colors hover:bg-primary/90 active:bg-primary/80"
        >
          <span className="text-xl font-bold lg:text-lg">Zahlen</span>
          <span className="text-[11px] opacity-75 lg:text-xs">
            {localSelected ? localSelected.name : '€ 0,00'}
          </span>
        </button>
      </div>
    </div>
  );
}
